/**
 * The narrative spine of a case — what happened, in order, and what we can see.
 *
 * A case file holds a feed, a graph, sagas and flows. All four are *indexes*:
 * they answer "what matched" sorted by time or by value. An exploit is not that
 * shape. It is a sequence of acts with a causal story — bought the position,
 * bridged it, inflated it, bridged it back, sold it, cashed out — and a reader
 * who does not already know the story cannot recover it from a table sorted
 * newest-first. That is why the public write-up of an incident reads better than
 * the product's own view of the same transactions.
 *
 * An act is deliberately small: a title, a note, the chains it happened on, and
 * the evidence pinned to it. Everything else the case already has.
 *
 * **The chains field is what makes an act honest.** Coverage is derived from it
 * ({@link actReach}), so an act on a chain no provider indexes is labelled as
 * unreachable rather than sitting there empty and reading as "nothing happened".
 * That distinction is the whole reason this is a first-class field rather than
 * something inferred from whatever evidence happened to be pinned: an act with no
 * evidence is exactly the case where the difference matters, and inferring the
 * chain from the evidence would be undefined precisely there.
 *
 * The Nesa / Cosmos-EVM exploit is the worked example. Five acts, two of them on
 * a chain the ingest cannot reach — and a case built only from what could be
 * fetched shows $50M appearing on Ethereum from nowhere.
 */
import { isIngestableChain, uncoveredChains } from "@authe/fe-commons/chains";
import { parseAssertedAmounts, type AssertedAmount } from "@authe/fe-commons/funds";

/** One step of the story. */
export interface CaseAct {
  /** Stable id, minted on create; the ordering is the array's. */
  id: string;
  title: string;
  /** What happened, in the author's words. May be empty while drafting. */
  note: string;
  /**
   * CAIP-2 chains this act took place on.
   *
   * Empty means "not stated" and is treated as unknown rather than as every
   * chain — a coverage claim about an act nobody has located would be a guess
   * presented as a fact.
   */
  chains: string[];
  /**
   * The phase this act belongs to (`"Bridge round trip"`); empty for none.
   *
   * Acts sharing a phase are read as ONE set — their movements netted together
   * (`stepFlow`, `@authe/fe-commons/funds`) — so the hop between two of them
   * cancels rather than being counted twice.
   */
  phase: string;
  /**
   * Amounts stated for what nobody can observe — the balance inflated on an
   * unindexed chain, the XMR sent into a swap service. Claims with a source,
   * shown beside the observed figures and never added to them.
   */
  asserted: AssertedAmount[];
}

/**
 * How many exhibits cite a given act, keyed by act id.
 *
 * Passed in rather than stored on the act. `CaseAct` used to carry
 * `evidence: string[]`, and nothing in the product ever wrote to it — so
 * {@link actReach} could not reach its `observed` branch and every step of every
 * story read as "not ingested yet" even for a case holding all of its
 * transactions. The citations live on the exhibit rows now
 * (`InvestigationEvidenceRow.slots`), which is where retention already looks:
 * an id array on the act would have been a citation pointing at a transaction
 * the case had since evicted.
 */
export type ActCitations = ReadonlyMap<string, number>;

/** No citations known — the honest default while the exhibit read is in flight. */
export const NO_CITATIONS: ActCitations = new Map<string, number>();

/** How much of an act the platform can actually show. */
export type ActReach =
  /** Evidence pinned; the act is on the record. */
  | { kind: "observed"; evidence: number }
  /** On chains we can fetch, but nothing pinned yet — ingest, then pin. */
  | { kind: "not-ingested" }
  /** On a chain no configured provider indexes. Annotation is the only record. */
  | { kind: "unreachable"; chains: string[] }
  /** No chains stated, so no claim either way. */
  | { kind: "unstated" };

/**
 * What the platform can say about this act.
 *
 * Evidence wins over coverage: an act with pinned transactions is observed even
 * if it also names an unreachable chain, because somebody has already put the
 * record there — most likely by pasting it through the `inline` source, which
 * needs no provider. Reporting that as "unreachable" would call the reader's own
 * work invisible.
 */
export function actReach(act: CaseAct, citations: ActCitations = NO_CITATIONS): ActReach {
  const cited = citations.get(act.id) ?? 0;
  if (cited > 0) return { kind: "observed", evidence: cited };
  if (act.chains.length === 0) return { kind: "unstated" };
  const uncovered = uncoveredChains(act.chains);
  // Unreachable only when NO chain on the act can be fetched. An act spanning
  // Ethereum and Nesa — a bridge leg — is ingestable in part, and calling it
  // unreachable would excuse leaving the half we can see unpinned.
  if (uncovered.length === act.chains.length) return { kind: "unreachable", chains: uncovered };
  return { kind: "not-ingested" };
}

/** One line explaining a reach, for the panel. Always says what to do next. */
export function reachExplanation(reach: ActReach): string {
  switch (reach.kind) {
    case "observed":
      return `${reach.evidence} transaction${reach.evidence === 1 ? "" : "s"} pinned as evidence.`;
    case "not-ingested":
      return "These chains can be fetched, but nothing is pinned yet — ingest the addresses on the Scope tab, then pin the transactions here.";
    case "unreachable":
      return "No configured provider indexes this chain, so a run cannot fetch it. Paste the transactions with the run dialog's Fixed payloads source and they join the case like any other.";
    case "unstated":
      return "No chain named, so there is nothing to say about whether this can be observed.";
  }
}

/**
 * Move one act up or down the story.
 *
 * The order IS the array — there is no `order` field to renumber — so a move is
 * a swap, and the whole array is what gets persisted. Returns the input
 * unchanged when the move would fall off either end, so a caller can bind the
 * control unconditionally and let it no-op rather than computing enabledness
 * twice.
 *
 * A story is the one view of a case that is ordered on purpose: the feed, the
 * graph and the flows all sort themselves, and this is the one a person arranges
 * to say what happened first. Being unable to reorder it meant the sequence was
 * decided by the order somebody happened to add the steps in — so getting it
 * right required deleting and retyping the ones that came out wrong, which loses
 * their citations with them.
 */
export function moveAct(acts: readonly CaseAct[], id: string, delta: -1 | 1): CaseAct[] {
  const from = acts.findIndex((act) => act.id === id);
  if (from < 0) return [...acts];
  const to = from + delta;
  if (to < 0 || to >= acts.length) return [...acts];
  const next = [...acts];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

export interface CaseCoverage {
  /** Every chain the case's acts name, de-duplicated in first-mention order. */
  chains: string[];
  /** The subset no configured provider can fetch. */
  uncovered: string[];
  /** Acts that can never carry fetched evidence. */
  unreachableActs: number;
  /** Acts on fetchable chains that have nothing pinned. */
  pendingActs: number;
}

/**
 * The case's coverage, in one read.
 *
 * Rendered ONCE at the top of the narrative rather than repeated per act,
 * because the sentence a reader needs before they interpret any of it is "two of
 * these five acts are on a chain we cannot fetch". Repeating that per act turns
 * a caveat about the case into noise on every row.
 */
export function caseCoverage(
  acts: readonly CaseAct[],
  citations: ActCitations = NO_CITATIONS,
): CaseCoverage {
  const chains: string[] = [];
  const seen = new Set<string>();
  let unreachableActs = 0;
  let pendingActs = 0;

  for (const act of acts) {
    for (const chain of act.chains) {
      const key = chain.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      chains.push(chain);
    }
    const reach = actReach(act, citations);
    if (reach.kind === "unreachable") unreachableActs += 1;
    if (reach.kind === "not-ingested") pendingActs += 1;
  }

  return { chains, uncovered: uncoveredChains(chains), unreachableActs, pendingActs };
}

/** Whether any chain in the set can be fetched at all. */
export function anyChainIngestable(chains: readonly string[]): boolean {
  return chains.some((chain) => isIngestableChain(chain));
}

/** A blank act, for the "add step" control. */
export function emptyAct(id: string): CaseAct {
  return { id, title: "", note: "", chains: [], phase: "", asserted: [] };
}

/**
 * Validate one persisted act, dropping anything malformed.
 *
 * Same discipline as `parseWatch`: `state.extra` is hand-editable and older
 * shapes exist, and an act with a non-array `chains` would break the panel that
 * renders it rather than simply being absent.
 *
 * A stored `evidence` array is ignored rather than migrated: nothing ever wrote
 * one, so every persisted value is `[]` and there is nothing in them to carry
 * across. Citations live on the exhibits now — see {@link ActCitations}.
 */
export function parseAct(raw: unknown, index: number): CaseAct | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const title = typeof row.title === "string" ? row.title : "";
  const id = typeof row.id === "string" && row.id ? row.id : `act-${index}`;
  if (!title.trim()) return null;
  const list = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && !!v) : [];
  return {
    id,
    title,
    note: typeof row.note === "string" ? row.note : "",
    chains: list(row.chains),
    phase: typeof row.phase === "string" ? row.phase : "",
    // Same leniency as the backend's coercer, so both agree on what survives.
    asserted: parseAssertedAmounts(row.asserted),
  };
}

export function parseActs(raw: unknown): CaseAct[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseAct).filter((a): a is CaseAct => a !== null);
}
